import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import HeaderCTA from "components/Shared/HeaderFilters/HeaderCTA"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import SearchBox from "components/Shared/HeaderFilters/SearchBox"
import { RPME_MODULE } from "constants/constants"
import { useAppDispatch, useAppSelector } from "hooks/hooksStore"
import { IRPMEBaseXlsForm } from "interfaces/dataInterfaces"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  useDeleteXlsFormSchemaMutation,
  useGetBaseXlsFormQuery,
  useGetXlsFormSchemasByIdQuery,
  useDownloadXlsFormSchemasByIdMutation,
  useDownloadJsonSchemasByIdMutation,
} from "services/rpme/xlsFormSchemas"
import { Column, ColumnShape } from "react-base-table"
import { Pagination } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import { faEye, faTrash } from "@fortawesome/free-solid-svg-icons"
import { getPageNumber, getSkip, getTop, setPageNumber, setTop } from "./baseXlsFormSlice"
import DetailsModal from "./DetailsModal"
import { formatDate } from "../../utils/helpers"
import DataTable from "../../components/Shared/DataTable/DataTable"

const permissionsProps = {
  moduleId: RPME_MODULE,
  subModule: "baseXLSForms",
}

export default function RPMEBaseXlsForm() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const pageNumber = useAppSelector(getPageNumber)
  const pageSize = useAppSelector(getTop)

  const top = useAppSelector(getTop)
  const skip = useAppSelector(getSkip)

  const [query, setQuery] = useState<string>("")
  const [actionItem, setActionItem] = useState<{ action: "delete" | "details"; data: IRPMEBaseXlsForm } | null>(null)

  const { data, isFetching } = useGetBaseXlsFormQuery({
    search: query,
    skip,
    take: top,
  })
  const [deleteSchema, { isLoading: isDeleting }] = useDeleteXlsFormSchemaMutation()

  const { data: xls, isLoading: loadingXLS } = useGetXlsFormSchemasByIdQuery(actionItem?.data?.id ?? 0, {
    skip: actionItem?.action !== "details",
  })

  const [download] = useDownloadXlsFormSchemasByIdMutation()
  const [downloadJson, { isLoading: loadingJson }] = useDownloadJsonSchemasByIdMutation()

  const onDeleteClick = (item: IRPMEBaseXlsForm) => setActionItem({ action: "delete", data: item })
  const onDetailsClick = (item: IRPMEBaseXlsForm) => setActionItem({ action: "details", data: item })

  const columns: Array<ColumnShape<IRPMEBaseXlsForm>> = [
    {
      key: "id",
      dataKey: "id",
      title: "ID",
      width: 80,
    },

    {
      key: "name",
      dataKey: "name",
      title: "XLSForm Name",
      width: 600,
      flexGrow: 1,
      resizable: true,
    },

    {
      key: "createDate",
      title: "Created Date",
      width: 200,
      cellRenderer: ({ rowData }) => formatDate(rowData.createDate),
    },
    {
      key: "userName",
      dataKey: "userName",
      title: "Created By",
      width: 300,
      minWidth: 200,
      resizable: true,
    },
    {
      key: "action",
      title: "Action",
      width: 350,
      align: Column.Alignment.CENTER,
      cellRenderer: ({ rowData }) => (
        <>
          <PermissionWrapper {...permissionsProps} feature="details">
            <Button
              action={() => onDetailsClick?.(rowData)}
              label="Details"
              className="btn-full blue action-btn"
              icon={faEye}
            />
          </PermissionWrapper>
          <PermissionWrapper {...permissionsProps} feature="delete">
            <Button
              action={() => onDeleteClick?.(rowData)}
              label="Delete"
              className="btn-full danger action-btn"
              style={{ marginLeft: "10px" }}
              icon={faTrash}
            />
          </PermissionWrapper>
        </>
      ),
    },
  ]

  return (
    <>
      <HeaderFilters
        left={<SearchBox onSubmit={setQuery} />}
        right={
          <PermissionWrapper {...permissionsProps} feature="addNew">
            <HeaderCTA onClick={() => navigate("add")} label="Add New Base XLSForm Schema" />
          </PermissionWrapper>
        }
      />

      {!!data && (
        <>
          <DataTable {...{ columns, isFetching: isFetching || isDeleting }} data={data?.items} rowKey="xlsFormId" />
          <Pagination
            totalItems={data?.count}
            pageSize={pageSize}
            pageSizes={[10, 20, 30]}
            page={pageNumber}
            onChange={(event): void => {
              dispatch(setPageNumber((event as any).page))
              dispatch(setTop((event as any).pageSize))
            }}
          />
        </>
      )}

      <DeleteConfirmationModal
        open={actionItem?.action === "delete"}
        onRequestClose={() => setActionItem(null)}
        onRequestSubmit={() => {
          deleteSchema(actionItem?.data.id as number)
          setActionItem(null)
        }}
      />

      {actionItem?.action === "details" && (
        <DetailsModal
          title="Base XLS Form Infos"
          xls={xls}
          loading={loadingXLS || loadingJson}
          download={download}
          downloadJSON={downloadJson}
          isOpen={actionItem.action === "details"}
          onClose={() => setActionItem(null)}
          module="rpme"
        />
      )}
    </>
  )
}
